import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../util/UserContext';
import {
  parseDestinationFromSearch,
  storePendingDestination,
} from '../../util/authDestination';

interface AuthLoginProps {
  /**
   * When provided, forces `AuthModal` into a specific mode on open.
   * - "signup": always open in sign-up mode (ignores existing-account cookie/localStorage)
   * - "signin": always open in sign-in mode
   */
  mode?: "signin" | "signup";
}

/**
 * Standalone signin/signup route (`/signin`, `/signup`).
 *
 * Responsibilities:
 *  1. Read any `?dest=` query param and persist it to sessionStorage so
 *     the Reddit-style "redirect back after login" flow survives
 *     OAuth popups and full-page redirects (the `AuthModal` itself
 *     may round-trip to Google/Facebook before the auth transition
 *     fires).
 *  2. Open the shared `AuthModal` for already-unauthenticated viewers.
 *
 * Post-auth navigation is handled centrally by `usePostAuthRedirect`
 * in `AppShell`, which consumes the stored destination on the
 * signed-out → signed-in transition. That means the same logic
 * applies whether the viewer completed auth from the modal on
 * `/chat`, a bookmarked `/signin?dest=/chat`, or an OAuth callback.
 */
const AuthLogin = ({ mode }: AuthLoginProps) => {
  const { currentUser, awaitingUser, setShowAuthModal, setAuthModalMode } = useUser();
  const location = useLocation();

  useEffect(() => {
    // Stash the intended destination from the URL as soon as this
    // page mounts, so the OAuth round-trip (which may remount this
    // route) does not lose the viewer's original intent. The
    // sanitizer drops open-redirect attempts silently, so a missing
    // or hostile `?dest` is indistinguishable from "go home after
    // auth" — the hook will fall back to `/`.
    const destFromQuery = parseDestinationFromSearch(location.search);
    if (destFromQuery) {
      storePendingDestination(destFromQuery);
    }
  }, [location.search]);

  useEffect(() => {
    if (!awaitingUser && !currentUser) {
      setAuthModalMode(mode ?? null);
      setShowAuthModal(true);
    }
    // Intentionally no `navigate('/')` on the authed branch: the
    // central `usePostAuthRedirect` hook owns that decision and will
    // route to the stored destination (or `/` when none is pending).
  }, [currentUser, awaitingUser, setShowAuthModal, setAuthModalMode, mode]);

  return null;
};

export default AuthLogin;
